'use client';

import { useState } from 'react';
import AppLogo from '@/components/ui/AppLogo';

const logoOptions = [
  {
    variant: 'option1' as const,
    name: 'NFC Wave + Book',
    description: 'Technology meets storytelling - NFC waves radiating from an open book',
    concept: 'Represents the fusion of physical tap technology with digital narratives',
  },
  {
    variant: 'option2' as const,
    name: 'Tag with Story Ribbon',
    description: 'Physical tag with flowing story elements and NFC indicator',
    concept: 'Emphasizes the tag-based nature and the flowing narrative of each item',
  },
  {
    variant: 'option3' as const,
    name: 'Fingerprint Bookmark',
    description: 'Unique identity lines within a bookmark shape',
    concept: 'Every collectible has a unique story - bookmark represents saved memories',
  },
  {
    variant: 'option4' as const,
    name: 'Treasure Chest with Signal',
    description: 'Heritage treasure chest with modern signal waves',
    concept: 'Your treasured collectibles connected to the digital world',
  },
  {
    variant: 'option5' as const,
    name: 'Premium Badge',
    description: 'Circular badge with NFC wave and book elements, elegant gold border',
    concept: 'Vintage-modern hybrid badge that feels like a premium collector\'s mark',
  },
  {
    variant: 'option6' as const,
    name: 'Wax Seal Stamp',
    description: 'Authentic wax seal appearance with TT monogram and tap indicator',
    concept: 'Premium collector\'s seal - each TapTale item is authenticated and unique',
  },
  {
    variant: 'option7' as const,
    name: 'Minimalist Emblem',
    description: 'Clean hexagonal badge combining tap gesture with book icon',
    concept: 'Modern and sophisticated - geometric precision meets storytelling',
  },
  {
    variant: 'option8' as const,
    name: 'Heritage Crest',
    description: 'Classic shield crest with scroll and NFC waves emanating from center',
    concept: 'Timeless heritage meets modern technology - protecting your stories',
  },
  {
    variant: 'option9' as const,
    name: 'Modern Stamp Badge',
    description: 'Contemporary rounded stamp with perforated edges and tap + book icon',
    concept: 'Collectible stamp aesthetic - each item is a story worth preserving',
  },
  {
    variant: 'option10' as const,
    name: 'Original + NFC Wave Signal',
    description: 'Original circular gold/crimson border with NFC wave signal icon in center',
    concept: 'Maintains premium aesthetic while clearly representing tap technology',
  },
  {
    variant: 'option11' as const,
    name: 'Original + Tap Gesture',
    description: 'Original circular gold/crimson border with finger tap gesture icon',
    concept: 'Direct representation of the tap interaction - intuitive and clear',
  },
  {
    variant: 'option12' as const,
    name: 'Original + NFC Chip',
    description: 'Original circular gold/crimson border with NFC chip technology symbol',
    concept: 'Technical and precise - shows the actual NFC technology inside',
  },
  {
    variant: 'option13' as const,
    name: 'Original + Wireless Signals',
    description: 'Original circular gold/crimson border with wireless signal waves',
    concept: 'Emphasizes the wireless connectivity and digital communication',
  },
  {
    variant: 'option14' as const,
    name: 'Original + Tag with Waves',
    description: 'Original circular gold/crimson border with tag icon and radio waves',
    concept: 'Combines physical tag concept with wireless technology signals',
  },
];

export default function LogoShowcase() {
  const [selectedVariant, setSelectedVariant] = useState<string>('option1');

  return (
    <section className="py-24 px-6 md:px-8 bg-gradient-to-b from-bg-primary via-bg-secondary to-bg-primary">
      <div className="max-w-container mx-auto">
        {/* Header */}
        <div className="text-center mb-16">
          <h2 className="font-serif text-4xl md:text-5xl font-light italic text-white mb-4">
            Logo Design Options
          </h2>
          <p className="text-gray-400 text-lg max-w-2xl mx-auto">
            Explore unique concepts for TapTale's brand identity. The last 5 options maintain the original premium circular design with gold/crimson border, featuring different NFC-related center icons.
          </p>
        </div>

        {/* Logo Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-16">
          {logoOptions.map((option) => (
            <div
              key={option.variant}
              onClick={() => setSelectedVariant(option.variant)}
              className={`group relative p-8 rounded-2xl border-2 transition-all duration-300 cursor-pointer ${
                selectedVariant === option.variant
                  ? 'border-accent-crimson bg-accent-crimson/5 shadow-[0_0_40px_rgba(220,38,38,0.15)]'
                  : 'border-white/10 bg-white/5 hover:border-white/20 hover:bg-white/10'
              }`}
            >
              {/* Selection indicator */}
              {selectedVariant === option.variant && (
                <div className="absolute top-4 right-4 w-6 h-6 rounded-full bg-accent-crimson flex items-center justify-center">
                  <svg className="w-4 h-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
              )}

              {/* Logo Preview */}
              <div className="flex flex-col items-center mb-6">
                <div className="mb-4 p-6 rounded-xl bg-bg-primary/50">
                  <AppLogo variant={option.variant} size={80} text="TapTale" />
                </div>
                <h3 className="font-serif text-2xl font-light text-white mb-2">{option.name}</h3>
              </div>

              {/* Description */}
              <div className="space-y-3">
                <p className="text-gray-300 text-sm leading-relaxed">{option.description}</p>
                <p className="text-gray-500 text-xs italic leading-relaxed">{option.concept}</p>
              </div>

              {/* Hover effect */}
              <div className="absolute inset-0 rounded-2xl bg-gradient-to-br from-accent-crimson/0 to-accent-gold/0 group-hover:from-accent-crimson/5 group-hover:to-accent-gold/5 transition-all duration-500 pointer-events-none" />
            </div>
          ))}
        </div>

        {/* Preview Section */}
        <div className="bg-gradient-to-br from-bg-secondary to-bg-primary rounded-3xl p-12 border border-white/10">
          <h3 className="font-serif text-3xl font-light italic text-white text-center mb-8">
            Preview in Context
          </h3>
          
          {/* Mock Header Preview */}
          <div className="bg-bg-primary/50 backdrop-blur-xl rounded-2xl p-6 border border-white/10">
            <div className="flex items-center justify-between">
              <AppLogo 
                variant={selectedVariant as any} 
                size={64} 
                text="TapTale" 
                className="transition-all duration-500"
              />
              <div className="flex items-center gap-6">
                <span className="text-sm text-gray-400">Features</span>
                <span className="text-sm text-gray-400">Pricing</span>
                <button className="px-6 py-2 rounded-full bg-accent-crimson text-white text-sm font-semibold">
                  Start Free
                </button>
              </div>
            </div>
          </div>

          {/* Size Variations */}
          <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="text-center">
              <p className="text-gray-500 text-xs uppercase tracking-wider mb-4">Large (64px)</p>
              <div className="flex justify-center items-center p-6 bg-bg-primary/30 rounded-xl">
                <AppLogo variant={selectedVariant as any} size={64} text="TapTale" />
              </div>
            </div>
            <div className="text-center">
              <p className="text-gray-500 text-xs uppercase tracking-wider mb-4">Medium (48px)</p>
              <div className="flex justify-center items-center p-6 bg-bg-primary/30 rounded-xl">
                <AppLogo variant={selectedVariant as any} size={48} text="TapTale" />
              </div>
            </div>
            <div className="text-center">
              <p className="text-gray-500 text-xs uppercase tracking-wider mb-4">Small (32px)</p>
              <div className="flex justify-center items-center p-6 bg-bg-primary/30 rounded-xl">
                <AppLogo variant={selectedVariant as any} size={32} text="TapTale" />
              </div>
            </div>
          </div>
        </div>

        {/* Implementation Note */}
        <div className="mt-12 text-center">
          <p className="text-gray-500 text-sm">
            To use a logo option, update the <code className="px-2 py-1 bg-white/5 rounded text-accent-gold">variant</code> prop in the Header component.
          </p>
          <p className="text-gray-600 text-xs mt-2">
            Example: <code className="px-2 py-1 bg-white/5 rounded text-gray-400">&lt;AppLogo variant="option1" ... /&gt;</code>
          </p>
        </div>
      </div>
    </section>
  );
}